import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useCustomDesign } from '@/contexts/CustomDesignContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Package,
  Move,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ShoppingCart,
  Palette,
  Image as ImageIcon,
  Download,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface PrintArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ProductView {
  image: string;
  printArea: PrintArea;
}

interface ProductMockup {
  id: string;
  name: string;
  category: string;
  basePrice: number;
  // Legacy support for single view
  image?: string;
  printArea?: PrintArea;
  // New multi-view support
  views?: {
    front: ProductView;
    back: ProductView;
    left?: ProductView;
    right?: ProductView;
  };
}

type ViewType = 'front' | 'back' | 'left' | 'right';

const productMockups: ProductMockup[] = [
  {
    id: 'tshirt-white',
    name: 'Classic White T-Shirt',
    category: 't-shirt',
    basePrice: 9.0,
    views: {
      front: {
        image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800&h=1000&fit=crop',
        printArea: { x: 35, y: 25, width: 30, height: 35 },
      },
      back: {
        image: 'https://images.unsplash.com/photo-1622445275463-afa2ab738c34?w=800&h=1000&fit=crop',
        printArea: { x: 35, y: 25, width: 30, height: 35 },
      },
      left: {
        image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=800&h=1000&fit=crop',
        printArea: { x: 40, y: 30, width: 20, height: 25 },
      },
      right: {
        image: 'https://images.unsplash.com/photo-1620799139834-6b8f844fbe29?w=800&h=1000&fit=crop',
        printArea: { x: 40, y: 30, width: 20, height: 25 },
      },
    },
  },
  {
    id: 'tshirt-black',
    name: 'Classic Black T-Shirt',
    category: 't-shirt',
    basePrice: 9.0,
    views: {
      front: {
        image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=800&h=1000&fit=crop',
        printArea: { x: 35, y: 25, width: 30, height: 35 },
      },
      back: {
        image: 'https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=800&h=1000&fit=crop',
        printArea: { x: 35, y: 25, width: 30, height: 35 },
      },
      left: {
        image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=800&h=1000&fit=crop',
        printArea: { x: 40, y: 30, width: 20, height: 25 },
      },
      right: {
        image: 'https://images.unsplash.com/photo-1620799139834-6b8f844fbe29?w=800&h=1000&fit=crop',
        printArea: { x: 40, y: 30, width: 20, height: 25 },
      },
    },
  },
  {
    id: 'hoodie-gray',
    name: 'Comfort Gray Hoodie',
    category: 'hoodie',
    basePrice: 18.5,
    image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&h=1000&fit=crop',
    printArea: { x: 35, y: 30, width: 30, height: 30 },
  },
  {
    id: 'mug-white',
    name: 'Ceramic White Mug',
    category: 'mug',
    basePrice: 5.5,
    image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=800&h=800&fit=crop',
    printArea: { x: 25, y: 35, width: 50, height: 30 },
  },
];

export const ProductCustomizer: React.FC = () => {
  const { designs, currentDesign, setCurrentDesign } = useCustomDesign();
  const [selectedProduct, setSelectedProduct] = useState<ProductMockup>(productMockups[0]);
  const [currentView, setCurrentView] = useState<ViewType>('front');
  const [designSize, setDesignSize] = useState<number>(100);
  const [designPosition, setDesignPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#FFFFFF');
  const [isExporting, setIsExporting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper to get current product image and print area
  const getCurrentViewData = useCallback(() => {
    if (selectedProduct.views) {
      const view = selectedProduct.views[currentView];
      if (view) {
        return view;
      }
      // Fallback to front view if current view not available
      return selectedProduct.views.front;
    }
    // Legacy single view support
    return {
      image: selectedProduct.image!,
      printArea: selectedProduct.printArea!,
    };
  }, [selectedProduct, currentView]);

  // Check if product supports multiple views
  const hasMultipleViews = selectedProduct.views !== undefined;

  // Get available views for the current product
  const getAvailableViews = useCallback((): ViewType[] => {
    if (!selectedProduct.views) return ['front'];
    const views: ViewType[] = ['front', 'back'];
    if (selectedProduct.views.left) views.push('left');
    if (selectedProduct.views.right) views.push('right');
    return views;
  }, [selectedProduct]);

  // Reset design position when product or view changes
  useEffect(() => {
    const viewData = getCurrentViewData();
    const area = viewData.printArea;
    setDesignPosition({
      x: area.x + area.width / 2,
      y: area.y + area.height / 2,
    });
  }, [selectedProduct, currentView, getCurrentViewData]);

  // Reset to front view when product changes
  useEffect(() => {
    setCurrentView('front');
  }, [selectedProduct]);

  const handleProductChange = (productId: string) => {
    const product = productMockups.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
    }
  };

  const handleDesignSelect = (designId: string) => {
    const design = designs.find((d) => d.id === designId);
    if (design) {
      setCurrentDesign(design);
      toast.success('Design selected');
    }
  };

  const handleViewChange = (view: ViewType) => {
    const availableViews = getAvailableViews();
    if (availableViews.includes(view)) {
      setCurrentView(view);
      toast.info(`Switched to ${view} view`);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!currentDesign) return;
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Constrain to print area
      const viewData = getCurrentViewData();
      const area = viewData.printArea;
      const constrainedX = Math.max(area.x, Math.min(x, area.x + area.width));
      const constrainedY = Math.max(area.y, Math.min(y, area.y + area.height));

      setDesignPosition({ x: constrainedX, y: constrainedY });
    },
    [isDragging, getCurrentViewData]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleReset = () => {
    setDesignSize(100);
    const viewData = getCurrentViewData();
    const area = viewData.printArea;
    setDesignPosition({
      x: area.x + area.width / 2,
      y: area.y + area.height / 2,
    });
    toast.info('Design reset to default position');
  };

  const handleAddToCart = () => {
    if (!currentDesign) {
      toast.error('Please select a design first');
      return;
    }

    // Here you would integrate with your cart system
    const customProduct = {
      product: selectedProduct,
      design: currentDesign,
      view: currentView,
      customization: {
        size: designSize,
        position: designPosition,
        color: selectedColor,
      },
      price: selectedProduct.basePrice,
    };

    console.log('Adding to cart:', customProduct);
    toast.success(`Added ${selectedProduct.name} with custom design to cart!`);
  };

  const viewData = getCurrentViewData();

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Product Customizer
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Drag, resize, and preview your design on different products
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Controls Panel */}
        <div className="space-y-4">
          {/* Product Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Select Product
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={selectedProduct.id} onValueChange={handleProductChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {productMockups.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - {product.basePrice.toFixed(3)} BHD
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Base Price:</span>
                <span className="font-semibold">
                  {selectedProduct.basePrice.toFixed(3)} BHD
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Design Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Your Designs
              </CardTitle>
              <CardDescription>
                {designs.length} design{designs.length !== 1 ? 's' : ''} available
              </CardDescription>
            </CardHeader>
            <CardContent>
              {designs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No designs yet. Create one in the Logo Generator or Image Editor!
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {designs.map((design) => (
                    <button
                      key={design.id}
                      onClick={() => handleDesignSelect(design.id)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                        currentDesign?.id === design.id
                          ? 'border-purple-600 ring-2 ring-purple-600 ring-offset-2'
                          : 'border-gray-200 hover:border-purple-400'
                      }`}
                    >
                      <img
                        src={design.imageUrl}
                        alt="Design"
                        className="w-full h-full object-contain bg-gray-50"
                      />
                      {currentDesign?.id === design.id && (
                        <Badge className="absolute top-1 right-1 bg-purple-600">
                          Selected
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customization Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Customize</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="size" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="size">Size</TabsTrigger>
                  <TabsTrigger value="color">Color</TabsTrigger>
                </TabsList>

                <TabsContent value="size" className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Design Size</label>
                      <span className="text-sm text-muted-foreground">{designSize}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ZoomOut className="h-4 w-4 text-muted-foreground" />
                      <Slider
                        value={[designSize]}
                        onValueChange={(value) => setDesignSize(value[0])}
                        min={50}
                        max={150}
                        step={5}
                        className="flex-1"
                        disabled={!currentDesign}
                      />
                      <ZoomIn className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  <Button
                    onClick={handleReset}
                    variant="outline"
                    className="w-full"
                    disabled={!currentDesign}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset Position
                  </Button>
                </TabsContent>

                <TabsContent value="color" className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Product Color
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {['#FFFFFF', '#000000', '#EF4444', '#3B82F6', '#10B981', '#FBBF24', '#8B5CF6', '#EC4899', '#6B7280', '#F59E0B'].map(
                        (color) => (
                          <button
                            key={color}
                            onClick={() => setSelectedColor(color)}
                            className={`w-full aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
                              selectedColor === color
                                ? 'border-purple-600 ring-2 ring-purple-600'
                                : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        )
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Add to Cart */}
          <Button
            onClick={handleAddToCart}
            disabled={!currentDesign}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            size="lg"
          >
            <ShoppingCart className="mr-2 h-5 w-5" />
            Add to Cart
          </Button>
        </div>

        {/* Preview Area */}
        <div className="lg:col-span-2">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Live Preview</span>
                {currentDesign && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Move className="h-3 w-3" />
                    Drag to reposition
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{selectedProduct.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* View Selector */}
              {hasMultipleViews && (
                <div className="flex justify-center">
                  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1">
                    {getAvailableViews().map((view) => (
                      <button
                        key={view}
                        onClick={() => handleViewChange(view)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          currentView === view
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-700 hover:bg-white hover:text-purple-600'
                        }`}
                      >
                        {view.charAt(0).toUpperCase() + view.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Preview */}
              <div
                ref={containerRef}
                className="relative aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden shadow-inner cursor-crosshair"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Product Image with Color Overlay */}
                <div className="absolute inset-0">
                  <img
                    src={viewData.image}
                    alt={`${selectedProduct.name} - ${currentView} view`}
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute inset-0 mix-blend-multiply opacity-60"
                    style={{ backgroundColor: selectedColor }}
                  />
                </div>

                {/* Print Area Guide (show when dragging) */}
                {isDragging && (
                  <div
                    className="absolute border-2 border-dashed border-purple-400 bg-purple-100/20"
                    style={{
                      left: `${viewData.printArea.x}%`,
                      top: `${viewData.printArea.y}%`,
                      width: `${viewData.printArea.width}%`,
                      height: `${viewData.printArea.height}%`,
                    }}
                  />
                )}

                {/* Design Overlay */}
                {currentDesign && (
                  <div
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${
                      isDragging ? 'cursor-grabbing' : 'cursor-grab'
                    }`}
                    style={{
                      left: `${designPosition.x}%`,
                      top: `${designPosition.y}%`,
                      width: `${(designSize / 100) * 30}%`,
                    }}
                    onMouseDown={handleMouseDown}
                  >
                    <img
                      src={currentDesign.imageUrl}
                      alt="Design"
                      className="w-full h-auto object-contain drop-shadow-2xl pointer-events-none"
                      draggable={false}
                    />
                  </div>
                )}

                {/* Placeholder */}
                {!currentDesign && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center space-y-3 p-6 bg-white/80 backdrop-blur-sm rounded-lg">
                      <ImageIcon className="h-12 w-12 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600 font-medium">
                        Select a design to preview
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions */}
              {currentDesign && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-900">
                    <strong>Tip:</strong> Click and drag your design to reposition it. Use the
                    size slider to adjust the scale.
                    {hasMultipleViews && ' Switch between views to see how your design looks from all angles.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
